import React from 'react';
import { Link } from 'react-router-dom';
import { Bike, Clock, ExternalLink, Facebook, Instagram, Mail, MapPin, Phone, Trees } from 'lucide-react';
import { PageLayout } from '../components/global/PageLayout';
import { PageIntro, Section } from '../components/ui/PageIntro';
import { ImageBox } from '../components/ui/ImageBox';
import { CTABlock } from '../components/ui/CTABlock';
import { IMAGES } from '../data/images';
import { ADDRESS_WITH_NAME, BRAND, CLOSED_DAY_NOTE, CONTACT, OPENING_HOURS, OPENING_HOURS_NOTE, PAGE_META, SOCIAL_LINKS } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function Visit() {
  useDocumentMeta({
    ...PAGE_META.visit,
    path: '/visit'
  });
  return <PageLayout data-id="element-789" data-mp="1g50ssvxb15gi">
      <PageIntro eyebrow="Visit us" title="Matlock Road, Brackenfield" intro="A gritstone building on the hill road out of town, with the Peak District about twenty minutes further up. Easy enough off the A38 and the A610, and you'll spot the old petrol-pump sign before you spot the door." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Visit'
    }]} image={IMAGES.frontDoor.src} data-id="element-790" data-mp="1k4jdprp4lgjc" />

      <Section data-id="element-791" data-mp="27nj7ylaumx5d">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14" data-id="element-792" data-mp="27p3z00xyfxyh">
          {/* Facts column: narrow and fixed beside the wide variable
              column, rather than a matched pair of equal cards. */}
          <div className="space-y-9" data-id="element-793" data-mp="30c6v6mf5rwlk">
            <div data-id="element-794" data-mp="2vdn8gjsqqgs5">
              <h2 className="flex items-center gap-2.5 font-bebas text-3xl tracking-wide text-ink" data-id="element-795" data-mp="2vd3pczrctmcj">
                <MapPin className="h-6 w-6 text-flame" aria-hidden="true" data-id="element-796" data-mp="2qmdj7kb3tfgs" />
                Where we are
              </h2>
              <address className="mt-4 text-lg font-bold not-italic leading-relaxed text-ink" data-id="element-797" data-mp="2lmsnrjv57p8t">
                {BRAND.name}
                <br data-id="element-798" data-mp="3j4iv72icalsj" />
                {CONTACT.address.line1}
                <br data-id="element-799" data-mp="3ec7tpv7yarxv" />
                {CONTACT.address.townOrCity}, {CONTACT.address.county}
                <br data-id="element-800" data-mp="1cepaxvv8vd0p" />
                {CONTACT.address.postcode}
              </address>
              <p className="mt-4 border-t border-hairline pt-4 text-sm font-medium leading-relaxed text-ink/70" data-id="element-801" data-mp="1ggasvvmmyhhr">
                {CONTACT.orientation}
              </p>
            </div>

            <div data-id="element-802" data-mp="17o8wq9jlnnda">
              <h2 className="flex items-center gap-2.5 font-bebas text-3xl tracking-wide text-ink" data-id="element-803" data-mp="17npc2l2kekvk">
                <Clock className="h-6 w-6 text-moss" aria-hidden="true" data-id="element-804" data-mp="sqgh7he1yyz3" />
                Opening hours
              </h2>
              <dl className="mt-4 space-y-2.5" data-id="element-805" data-mp="xqui8fh8l8nm">
                {OPENING_HOURS.map(row => <div key={row.days} className="flex justify-between gap-4 border-b border-hairline pb-2.5 text-sm last:border-0" data-id="element-806" data-mp="jrldqd2e82zh">
                    <dt className="font-bold text-ink/85" data-id="element-807" data-mp="2e5cjh1hlq2pn">{row.days}</dt>
                    <dd className="shrink-0 text-ink/65" data-id="element-808" data-mp="2j34a7s6ifis9">{row.hours}</dd>
                  </div>)}
              </dl>
              {/* Monday is stated twice on purpose: once as a row, once as
                  a sentence. It is the single fact on this page that turns
                  a forty-minute ride into a wasted journey. */}
              <p className="mt-4 border-t border-hairline pt-3 font-bold text-ink" data-id="element-809" data-mp="3217grx9cbcye">
                {CLOSED_DAY_NOTE}
              </p>
              {/* Defers to the Google listing, which the client keeps
                  current and which is where bank-holiday changes actually
                  appear. Registry I10. */}
              <p className="mt-2 text-sm text-ink/60" data-id="element-810" data-mp="3713l5xly96eh">{OPENING_HOURS_NOTE}</p>
            </div>

            {/* Confirmed by the client 2026-09-12: there is a proper car
                park with room for bikes and cars. Stated without a figure,
                because no capacity is on record and a number is what a
                club secretary would plan around. Registry D46. */}
            <div data-id="element-811" data-mp="1mkyym7v2pke3">
              <h2 className="flex items-center gap-2.5 font-bebas text-3xl tracking-wide text-ink" data-id="element-812" data-mp="1o01of51i1rt3">
                <Bike className="h-6 w-6 text-rust" aria-hidden="true" data-id="element-813" data-mp="1sz4ijufxydma" />
                Parking
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-ink/80" data-id="element-814" data-mp="1x0zkn15p4v14">
                <li className="flex items-start gap-2.5" data-id="element-815" data-mp="21zu8wimfnbsm">
                  <span aria-hidden="true" className="mt-1 font-bold text-rust" data-id="element-816" data-mp="21zasx7gcexh8">
                    ●
                  </span>
                  There's a good-sized car park, with plenty of room for bikes
                  and cars both. Free, no permit
                </li>
                <li className="flex items-start gap-2.5" data-id="element-817" data-mp="1e9707v4p0i50">
                  <span aria-hidden="true" className="mt-1 font-bold text-rust" data-id="element-818" data-mp="1j0ygncu4x7hf">
                    ●
                  </span>
                  Riders tend to park up together, and there's space for a
                  club or a group turning up at once
                </li>
                <li className="flex items-start gap-2.5" data-id="element-819" data-mp="qiuhdp7wt0zn">
                  <span aria-hidden="true" className="mt-1 font-bold text-rust" data-id="element-820" data-mp="j1eg7fpnzukp">
                    ●
                  </span>
                  The Fuel Stop truck is out in the car park, so you can park
                  up and order without going far
                </li>
              </ul>
            </div>

            {/* Both facts come from the signage in `gardenCorridor`. The
                beer garden was not mentioned anywhere on the site, and a
                single unisex toilet is worth stating plainly rather than
                letting someone discover it. Registry D34. */}
            <div data-id="element-821" data-mp="3mh3jqeiaqtga">
              <h2 className="flex items-center gap-2.5 font-bebas text-3xl tracking-wide text-ink" data-id="element-822" data-mp="3mimz82w4p0h0">
                <Trees className="h-6 w-6 text-moss" aria-hidden="true" data-id="element-823" data-mp="3hk3e24pczspp" />
                While you're here
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-ink/80" data-id="element-824" data-mp="3dj9l2wx27npy">
                <li className="flex items-start gap-2.5" data-id="element-825" data-mp="38ivp4d8qg48a">
                  <span aria-hidden="true" className="mt-1 font-bold text-moss" data-id="element-826" data-mp="2yu89uznn7wey">
                    ●
                  </span>
                  There's a beer garden out the back, through the corridor past
                  the bar
                </li>
                <li className="flex items-start gap-2.5" data-id="element-827" data-mp="2oyer2yjbz6oq">
                  <span aria-hidden="true" className="mt-1 font-bold text-moss" data-id="element-828" data-mp="2twg698dmylux">
                    ●
                  </span>
                  There's one toilet, unisex, on the same corridor. It isn't
                  an accessible one
                </li>
              </ul>
            </div>

            <div data-id="element-829" data-mp="37u4tunz3873o">
              <h2 className="font-bebas text-3xl tracking-wide text-ink" data-id="element-830" data-mp="37umvy18flfji">
                Get hold of us
              </h2>
              <ul className="mt-4 space-y-2" data-id="element-831" data-mp="3cs6dba0fe8fo">
                <li data-id="element-832" data-mp="4e5zjzdl6x1c">
                  <a href={CONTACT.telephoneHref} className="flex items-center gap-2.5 py-1.5 font-bold text-ink transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-833" data-mp="4cuyw8ffu5eg">
                    <Phone className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-834" data-mp="1ovypz5bmoqzv" />
                    {CONTACT.telephone}
                  </a>
                </li>
                <li data-id="element-835" data-mp="1awpbca32m4y0">
                  <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 py-1.5 font-bold text-ink transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-836" data-mp="1aw782thzqqgl">
                    <Mail className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-837" data-mp="1ey12wl5i908u" />
                    {CONTACT.email}
                  </a>
                </li>
                {/* The quickest route to an answer in practice, so it sits
                    with the telephone and the email rather than only in the
                    footer. Both leave the site and say so. */}
                {SOCIAL_LINKS.map(social => {
                const Icon = social.platform === 'facebook' ? Facebook : Instagram;
                return <li key={social.label} data-id="element-838" data-mp="3o8jn92h75wts">
                      <a href={social.href} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 py-1.5 font-bold text-ink transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-839" data-mp="3t6bs0x2xxdfe">
                        <Icon className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-840" data-mp="28q87a6vjd6u7" />
                        {social.label}
                        <ExternalLink className="h-3 w-3 shrink-0 text-ink/40" aria-hidden="true" data-id="element-841" data-mp="2580xwworto86" />
                        <span className="sr-only" data-id="element-842" data-mp="20fhng8ldr97l">
                          , {BRAND.name} on {social.label}, opens in a new
                          tab
                        </span>
                      </a>
                    </li>;
              })}
              </ul>
              <p className="mt-3 text-sm leading-relaxed text-ink/65" data-id="element-843" data-mp="2o52gjkb8zmul">
                Facebook is the one we post to first, so that's the fastest
                way to see what's happening this week.
              </p>
            </div>
          </div>

          <div data-id="element-844" data-mp="2a86rwjiijmtf">
            <figure className="overflow-hidden rounded-container border border-hairline bg-white" data-id="element-845" data-mp="2a6lxqv0425w3">
              <ImageBox src={IMAGES.buildingFromRoad.src} alt={IMAGES.buildingFromRoad.alt} ratio="4/3" data-id="element-846" data-mp="13s36mabtda91" />
              <figcaption className="border-t border-hairline px-4 py-3 text-sm text-ink/65" data-id="element-847" data-mp="18rkt1qges59n">
                The old petrol-pump sign on the road, with the terrace behind
                it.
              </figcaption>
            </figure>

            {/* Still no embedded map — that needs a provider (I08) — but the
                address is confirmed now, so a plain search link to the
                postcode is honest and immediately useful. It carries the
                confirmed postcode, so it cannot point anywhere else. */}
            <div className="mt-6 rounded-container border border-hairline bg-cream p-5" data-id="element-848" data-mp="1mizdm1pyseit">
              <h2 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-849" data-mp="20hz2nahe2vfi">
                Finding the turning
              </h2>
              <p className="mt-2 max-w-prose leading-relaxed text-ink/75" data-id="element-850" data-mp="1wfki8ly2nnm4">
                Coming from the A38, take the Alfreton junction and follow
                Matlock Road up towards Brackenfield. We're the stone building
                on the left behind the dry-stone wall. Look out for the old
                petrol pump on a post at the roadside.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-ink/70" data-id="element-851" data-mp="3s66tj0frijw8">
                Putting{' '}
                <strong className="font-bold text-ink" data-id="element-852" data-mp="3eesa4shy0gon">
                  {CONTACT.address.postcode}
                </strong>{' '}
                into your satnav will bring you to the door.
              </p>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS_WITH_NAME)}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-container border border-control bg-white px-4 py-2.5 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-853" data-mp="34fsxyzxyy2ww">
                <MapPin className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-854" data-mp="w9it4lu0rcnh" />
                Open in Google Maps
                <ExternalLink className="h-3.5 w-3.5 text-ink/50" aria-hidden="true" data-id="element-855" data-mp="s85je0nt2pba" />
                <span className="sr-only" data-id="element-856" data-mp="n9ly82h1dhjz">(opens in a new tab)</span>
              </a>
            </div>

            {/* Two pictures, two different questions: what it looks like
                out on the deck, and what the inside is actually shaped
                like. The passage shot is the one that carries the steps and
                the low beams the paragraph below mentions. */}
            <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-857" data-mp="166n6px9stzso">
              <figure className="overflow-hidden rounded-container border border-hairline bg-white" data-id="element-858" data-mp="118vcgwlom1lb">
                <ImageBox src={IMAGES.deckView.src} alt={IMAGES.deckView.alt} ratio="4/3" data-id="element-859" data-mp="1ynprf6fsv1nz" />
                <figcaption className="border-t border-hairline px-4 py-3 text-sm text-ink/65" data-id="element-860" data-mp="1tvy5xabi3m4p">
                  The raised deck, and the reason people sit on it.
                </figcaption>
              </figure>
              <figure className="overflow-hidden rounded-container border border-hairline bg-white" data-id="element-861" data-mp="269bpdvokk8z2">
                <ImageBox src={IMAGES.beamedPassage.src} alt={IMAGES.beamedPassage.alt} ratio="4/3" data-id="element-862" data-mp="2b8y3068b7r8e" />
                <figcaption className="border-t border-hairline px-4 py-3 text-sm text-ink/65" data-id="element-863" data-mp="2k6zzq9l0fxpv">
                  Low beams, flagstones and a step or two between rooms.
                </figcaption>
              </figure>
            </div>

            <div className="mt-10" data-id="element-864" data-mp="2yw65kgecsm6q">
              <h2 className="font-bebas text-3xl tracking-wide text-ink" data-id="element-865" data-mp="2yv4l5lbg6ncc">
                What the place is
              </h2>
              <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-866" data-mp="33x3nqszd5g88">
                A gritstone café on the hill road at Brackenfield, with a
                flagged terrace at the front and a raised timber deck looking
                out across the valley. Whether you're a rider after decent
                coffee before heading up into the Peak District, or a local
                after a proper plate of food, there's a place for you here.
              </p>
              <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-867" data-mp="e8cnahwsq0pl">
                Inside is half a dozen small rooms of stone walls and low
                beams, with veteran motorcycles up on them, enamel petrol
                signs, race photographs and gig posters. There's a coffee
                bar, a cabinet of cakes, the ice cream counter and a proper
                bar. Friday is bike night, every week all year round, and the
                car park fills right up.
              </p>

              {/* Worth saying on the Visit page specifically. Someone
                  deciding whether they can get in and around needs the
                  shape of the building, and the photographs show steps,
                  low beams and narrow doorways. Registry L02. */}
              <p className="mt-4 max-w-prose leading-relaxed text-ink/75" data-id="element-868" data-mp="1ihmmmdheqlxj">
                It's an old building, so there's a fair bit worth knowing
                before you set off. There are{' '}
                <strong className="font-bold text-ink" data-id="element-869" data-mp="19gooucxkbane">
                  steps up to the main entrance
                </strong>
                , and inside you'll find low beams, flagstones and a step or
                two between rooms. We've a sign up by the bar asking you to
                mind your head and the step, and we mean it.{' '}
                <strong className="font-bold text-ink" data-id="element-870" data-mp="1ser80b3fidda">
                  We don't have an accessible toilet
                </strong>{' '}
                — the one toilet is unisex, along the corridor from the bar.
                If you want to know whether the place works for you, give us a
                ring and we'll talk you through it honestly.
              </p>

              <ul className="mt-7 flex flex-wrap gap-x-8 gap-y-4" data-id="element-871" data-mp="2up7rm2pgq7vi">
                <li data-id="element-872" data-mp="2ptioxqi5cauz">
                  <Link to="/whats-on" className="font-bold text-flame underline decoration-flame/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-873" data-mp="2pt0ik11rsg9c">
                    What's on this month
                  </Link>
                </li>
                <li data-id="element-874" data-mp="26zmw6kc4hi5c">
                  <Link to="/food-and-drink" className="font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-875" data-mp="222mrodux8ngc">
                    The Fuel Stop menu
                  </Link>
                </li>
                <li data-id="element-876" data-mp="3l34ejyhvtr1">
                  <Link to="/gallery" className="font-bold text-moss underline decoration-moss/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-moss focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-877" data-mp="3lmbf5mlwxqa">
                    Have a look round
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <CTABlock data-id="element-878" data-mp="3l1aytd3gni1k" />
    </PageLayout>;
}